import React, { useState } from "react";
import { Grid } from "@mui/material";
import { useSelector } from "react-redux";

import RpmAnalytic from "./rpm/RpmAnalytic";
import Loading from "components/common/loading/Loading";
import PatientAnalyticChart from "./PatientAnalyticChart";
import EmptyContainer from "components/common/empty_container/EmptyContainer";
import {
  useGetPatientAnalyticsQuery,
  useGetRpmAnalyticsQuery,
  useGetWeightScaleQuery,
} from "app/store/patient/api/patientApi";
import ScaleChart from "components/pages/rpm/chart/ScaleChart";
import { some } from "lodash";
import { RPM_DEVICE_MANUFACTURER } from "static/constants";
import { TREND_CHARTS } from "./constants.js";

const isArrayEmptyOrNull = (arr) => {
  if (!arr) {
    return true;
  }
  return arr.every((value) => value === null);
};

const renderTrendCharts = (data, charts) =>
  charts.map(
    ({ key, title, durationKey }) =>
      !isArrayEmptyOrNull(data?.[key]) && (
        <Grid item sm={6} md={4} key={key}>
          <PatientAnalyticChart
            chartData={{
              title,
              labels: data?.dates,
              data: data?.[key],
              duration: durationKey ? data?.[durationKey] : undefined,
            }}
          />
        </Grid>
      )
  );

const checkIfAllAnalyticsDataIsEmpty = (data) => {
  if (!data) {
    return true;
  }
  const { dates, ...otherTrendsData } = data;
  for (const key in otherTrendsData) {
    const isAllDataEmptyOrNull = isArrayEmptyOrNull(data[key]);
    if (!isAllDataEmptyOrNull) {
      return false;
    }
  }
  return true;
};

const checkIfRpmAnalyticsDataIsEmpty = (data) => {
  if (!data) {
    return true;
  }
  const { diastolic, systolic } = data;
  if (!!diastolic?.allData?.length || !!systolic?.allData?.length) {
    return false;
  }
  return true;
};

const PatientAnalytics = ({ patientId: patient_id }) => {
  const { patientId, deviceInformation } = useSelector(
    (state) => state.transmission
  );

  const patientID = patientId || patient_id;

  const [filter, setFilter] = useState("last_thirty_one_days");
  const { data: RpmAnalyticData, isLoading: rmpLoading } =
    useGetRpmAnalyticsQuery(
      { patientId: patientID, filter },
      { refetchOnMountOrArgChange: true }
    );

  const { data, isLoading } = useGetPatientAnalyticsQuery(
    { patientId: patientID },
    { refetchOnMountOrArgChange: true }
  );

  const checkPatientHasScale = () =>
    deviceInformation?.rpm_devices &&
    some(deviceInformation.rpm_devices, {
      rpmDeviceName: RPM_DEVICE_MANUFACTURER.scale,
    });

  const { data: scaleChartData, isLoading: isScaleChartLoading } =
    useGetWeightScaleQuery(patientID, {
      refetchOnMountOrArgChange: true,
    });

  if (isLoading || rmpLoading || isScaleChartLoading)
    return (
      <div className="patient-modal__section" style={{ border: "none" }}>
        <Loading height="150px" />
      </div>
    );

  const isAllPatientAnalyticsDataEmptyOrNull =
    checkIfAllAnalyticsDataIsEmpty(data);

  const isAllRPMAnalyticDataIsEmptyOrNull =
    checkIfRpmAnalyticsDataIsEmpty(RpmAnalyticData);

  const isScaleDataEmpty =
    scaleChartData && Object.values(scaleChartData).length > 0
      ? Object.values(scaleChartData).some((value) => isArrayEmptyOrNull(value))
      : true;

  return (
    <div className="patient-modal__section" style={{ border: "none" }}>
      {isAllPatientAnalyticsDataEmptyOrNull &&
        isAllRPMAnalyticDataIsEmptyOrNull &&
        (isScaleDataEmpty || checkPatientHasScale()) && (
          <EmptyContainer
            noDataTextStyles={{
              textAlign: "center",
            }}
            noDataText={
              <>
                <h3 className="empty__title">No records found</h3>
              </>
            }
          />
        )}

      <>
        {!isAllPatientAnalyticsDataEmptyOrNull && (
          <>
            <h2 style={{ fontSize: "18px", marginBottom: "10px" }}>
              CIED Trends Information
            </h2>
            <Grid container spacing={2}>
              {renderTrendCharts(data, TREND_CHARTS)}
            </Grid>
          </>
        )}
        {!isAllRPMAnalyticDataIsEmptyOrNull && (
          <RpmAnalytic
            filter={filter}
            isLoading={rmpLoading}
            setFilter={setFilter}
            data={RpmAnalyticData}
          />
        )}
        {!isScaleDataEmpty && checkPatientHasScale() && (
          <ScaleChart patientId={patientID} height={80} />
        )}
      </>
    </div>
  );
};

export default PatientAnalytics;
